/**
 * The save manager: the right-hand half of the home screen.
 *
 * Lists the player's named saves with their dates, lets one be made, renamed,
 * loaded or deleted, and keeps the four file-and-slot actions that used to sit
 * loose on the pause screen.
 *
 * **Why every destructive button asks first.** Loading a save replaces the
 * world you are standing in and deleting one is final. Both are one click away
 * from where a person clicks to resume the game, so both confirm - and the
 * confirmation names the save, because "are you sure?" about an unnamed thing
 * is a question nobody can answer.
 *
 * DOM only. What a slot is called and how the list is ordered live in
 * core/save/slots.ts, where they can be tested without a browser.
 */

import {
  playerSlots, describeSlot, slotNameProblem, suggestSlotName, MAX_SLOT_NAME,
  LIVE_SLOT, BACKUP_SLOT, worldSlots,
  type SlotView, type NameProblem, type WorldSlot,
} from "../core/save/slots.js";
import { ui, type Lang, type UiKey } from "../core/text/lang.js";

export interface SaveActions {
  /** Every stored key with its date and size. */
  list: () => Promise<{ name: string; savedAt: number; bytes: number }[]>;
  /** Copy the live world into a named slot. */
  saveAs: (name: string) => Promise<void>;
  rename: (from: string, to: string) => Promise<void>;
  load: (name: string) => Promise<void>;
  remove: (name: string) => Promise<void>;
  /** Carry on with a map's own world. */
  playWorld: (mapId: string) => Promise<void>;
  /** What a map is called, in the player's language. */
  mapName: (mapId: string) => string;
  /** Every map the game has, and which one owns the unsuffixed live slot. */
  maps: () => { maps: readonly { readonly id: string }[]; defaultMapId: string };
  exportFile: () => void;
  importFile: () => Promise<void>;
  restoreBackup: () => Promise<void>;
  startFresh: () => Promise<void>;
}

export class SavesUI {
  private worlds: WorldSlot[] = [];
  private readonly statusEl: HTMLDivElement;
  private readonly listEl: HTMLDivElement;
  private readonly nameInput: HTMLInputElement;
  private readonly problemEl: HTMLDivElement;
  private readonly headingEl: HTMLDivElement;
  private slots: SlotView[] = [];
  /** Set by main.ts. Call `refresh` after changing it. */
  lang: Lang = "uk";
  /**
   * True while playing on somebody else's world.
   *
   * The panel then offers nothing: the world is the server's, it keeps it for
   * everybody at once, and there is no such thing as your own copy of it.
   * Leaving the buttons up would be worse than useless - "Зберегти" would
   * write a world that is not yours into the slot where yours lives, which is
   * the one mistake the map picker exists to prevent, arriving by a third
   * door.
   */
  onServer = false;
  /*
   * The buttons built once, in the constructor.
   *
   * `refresh` redraws the list and the status lines, so those pick a new
   * language up on their own - these did not, and the panel stayed Ukrainian
   * inside an English game. Held so they can be written again.
   */
  private readonly fixed: { el: HTMLButtonElement; key: UiKey }[] = [];

  constructor(
    private readonly host: HTMLElement,
    private readonly actions: SaveActions,
  ) {
    /*
     * Every click here is stopped from reaching the overlay.
     *
     * The overlay turns any click into a request for pointer lock, which
     * starts the game - so without this, reaching for "delete" would begin
     * playing instead. Caught once at the top rather than on each button,
     * because the version that did it per button grew a new one and lost.
     */
    this.host.addEventListener("click", (e) => e.stopPropagation());
    this.host.addEventListener("mousedown", (e) => e.stopPropagation());

    /*
     * What is in the two slots the player cannot name.
     *
     * They are deliberately not rows in the list - a «Видалити» button on the
     * world you are standing in is not a thing this panel should have - but
     * hiding them entirely made «Повернути попередній» look broken: with an
     * empty backup slot it does nothing, says nothing, and there was no way
     * to find out that there was nothing to return to.
     */
    this.statusEl = document.createElement("div");
    this.statusEl.className = "save-status";

    const maker = document.createElement("div");
    maker.className = "save-maker";

    this.nameInput = document.createElement("input");
    this.nameInput.type = "text";
    this.nameInput.className = "save-name";
    this.nameInput.maxLength = MAX_SLOT_NAME;
    this.nameInput.placeholder = ui("saves-name-placeholder", this.lang);
    this.nameInput.addEventListener("keydown", (e) => {
      // The overlay and the game both listen for keys; a name with a "w" in
      // it must not walk the player forward.
      e.stopPropagation();
      if (e.key === "Enter") void this.saveAs();
    });

    const saveButton = document.createElement("button");
    saveButton.type = "button";
    saveButton.className = "save-button primary";
    this.fixed.push({ el: saveButton, key: "saves-save" });
    saveButton.addEventListener("click", () => void this.saveAs());

    maker.append(this.nameInput, saveButton);

    this.problemEl = document.createElement("div");
    this.problemEl.className = "save-problem";
    this.problemEl.hidden = true;

    this.listEl = document.createElement("div");
    this.listEl.className = "save-list";

    const others = document.createElement("div");
    others.className = "save-others";
    const heading = document.createElement("div");
    heading.className = "save-heading";
    this.headingEl = heading;
    others.append(heading);
    for (const [key, run] of [
      ["saves-export", (): void => this.actions.exportFile()],
      ["saves-import", (): void => void this.actions.importFile()],
      ["saves-restore", (): void => void this.actions.restoreBackup()],
      ["saves-fresh", (): void => void this.actions.startFresh()],
    ] as const) {
      const b = document.createElement("button");
      b.type = "button";
      b.className = "save-button";
      this.fixed.push({ el: b, key });
      b.addEventListener("click", run);
      others.append(b);
    }

    this.host.append(this.statusEl, maker, this.problemEl, this.listEl, others);
    this.retitle();
  }

  /** Write every fixed caption again, in the language now in force. */
  retitle(): void {
    for (const { el, key } of this.fixed) el.textContent = ui(key, this.lang);
    this.headingEl.textContent = ui("saves-files", this.lang);
    this.nameInput.placeholder = ui("saves-name-placeholder", this.lang);
  }

  /** Read the store and redraw. Called every time the panel is opened. */
  async refresh(): Promise<void> {
    this.retitle();
    /*
     * Somebody else's world has no local saves, and never will.
     *
     * One line instead of a panel, rather than buttons that are disabled: a
     * greyed-out "Зберегти" invites the question of how to un-grey it, and
     * the answer is "leave the game you are playing".
     */
    if (this.onServer) {
      this.statusEl.replaceChildren(this.emptyRow(ui("saves-on-server", this.lang)));
      this.listEl.replaceChildren();
      this.problemEl.textContent = "";
      for (const { el } of this.fixed) el.hidden = true;
      this.nameInput.hidden = true;
      return;
    }
    for (const { el } of this.fixed) el.hidden = false;
    this.nameInput.hidden = false;
    let rows: { name: string; savedAt: number; bytes: number }[];
    try {
      rows = await this.actions.list();
    } catch (error) {
      /*
       * Say so, and still draw the panel.
       *
       * Returning here left the window with no status lines and no buttons
       * captioned - a blank right-hand half that looked like the game had
       * hung rather than like storage being unavailable.
       */
      console.error("could not list saves", error);
      this.slots = [];
      this.drawStatus([]);
      this.listEl.replaceChildren(this.emptyRow(ui("saves-unreadable", this.lang)));
      return;
    }
    this.slots = playerSlots(rows);
    const { maps, defaultMapId } = this.actions.maps();
    this.worlds = worldSlots(rows, maps, defaultMapId);
    this.drawStatus(rows);
    this.draw();
    if (this.nameInput.value.trim() === "") {
      this.nameInput.value = suggestSlotName(this.slots.map((s) => s.name));
    }
  }

  /** The live world and the backup, as two plain lines of fact. */
  private drawStatus(
    rows: readonly { name: string; savedAt: number; bytes: number }[],
  ): void {
    const find = (key: string): { savedAt: number; bytes: number } | undefined =>
      rows.find((r) => r.name === key);
    const lines: HTMLElement[] = [];
    for (const [key, label, missing] of [
      [LIVE_SLOT, "saves-live", "saves-live-none"],
      [BACKUP_SLOT, "saves-backup", "saves-backup-none"],
    ] as const) {
      const row = document.createElement("div");
      row.className = "save-status-row";
      const what = document.createElement("span");
      what.className = "save-status-label";
      what.textContent = ui(label, this.lang);
      const value = document.createElement("span");
      const found = find(key);
      value.className = found === undefined ? "save-status-none" : "save-status-when";
      value.textContent = found === undefined
        ? ui(missing, this.lang) : describeSlot(found);
      row.append(what, value);
      lines.push(row);
    }
    this.statusEl.replaceChildren(...lines);
  }

  private emptyRow(text: string): HTMLDivElement {
    const el = document.createElement("div");
    el.className = "save-empty";
    el.textContent = text;
    return el;
  }

  private draw(): void {
    /*
     * The maps' own worlds first, then anything named.
     *
     * First because they are what somebody is looking for: "the пригода I
     * was in yesterday" is a world the game kept on its own, and it used to
     * be invisible here - on disk, unlisted, with nowhere to click. Named
     * saves are deliberate and rarer, so they come under.
     *
     * "There is nothing here yet" is about *both* lists being empty, and it
     * used to be about one. The test for it sat at the top of this method
     * and asked only about named saves, so a player with two worlds the game
     * had kept and nothing of their own saw "збережень ще немає" over an
     * empty box - and the rows below the early return were never reached.
     * Both the fix and the checks written with it missed this, because every
     * one of them made a named save as well.
     */
    if (this.slots.length === 0 && this.worlds.length === 0) {
      this.listEl.replaceChildren(
        this.emptyRow(ui("saves-none", this.lang)),
      );
      return;
    }
    this.listEl.replaceChildren(
      ...this.worlds.map((world) => this.worldRow(world)),
      ...this.slots.map((slot) => this.row(slot)),
    );
  }

  /**
   * A map's own world: playable, and nothing else.
   *
   * No rename and no delete. It is not a save somebody made and named - it
   * is where that map is up to, and the game writes it again half a minute
   * later. A «Видалити» on it would be a button that appears to do something
   * and does not.
   */
  private worldRow(world: WorldSlot): HTMLDivElement {
    const el = document.createElement("div");
    el.className = "save-row";

    const text = document.createElement("div");
    text.className = "save-text";
    const name = document.createElement("div");
    name.className = "save-row-name";
    name.textContent = this.actions.mapName(world.mapId);
    const when = document.createElement("div");
    when.className = "save-when";
    when.textContent = `${ui("saves-world-kept", this.lang)} · ` +
      describeSlot({ savedAt: world.savedAt, bytes: world.bytes });
    text.append(name, when);

    const buttons = document.createElement("div");
    buttons.className = "save-row-buttons";
    const play = document.createElement("button");
    play.type = "button";
    play.className = "save-button primary";
    play.textContent = ui("saves-play", this.lang);
    play.addEventListener("click", () => {
      void this.actions.playWorld(world.mapId);
    });
    buttons.append(play);

    el.append(text, buttons);
    return el;
  }

  private row(slot: SlotView): HTMLDivElement {
    const el = document.createElement("div");
    el.className = "save-row";

    const text = document.createElement("div");
    text.className = "save-text";
    const name = document.createElement("div");
    name.className = "save-row-name";
    name.textContent = slot.name;
    const when = document.createElement("div");
    when.className = "save-when";
    when.textContent = describeSlot(slot);
    text.append(name, when);

    const buttons = document.createElement("div");
    buttons.className = "save-row-buttons";
    for (const [key, cls, run] of [
      ["saves-play", "primary", (): void => void this.load(slot)],
      ["saves-rename", "", (): void => void this.rename(slot)],
      ["saves-delete", "danger", (): void => void this.remove(slot)],
    ] as const) {
      const b = document.createElement("button");
      b.type = "button";
      b.className = `save-button ${cls}`.trim();
      b.textContent = ui(key, this.lang);
      b.addEventListener("click", run);
      buttons.append(b);
    }

    el.append(text, buttons);
    return el;
  }

  private problem(message: string | null): void {
    this.problemEl.textContent = message ?? "";
    this.problemEl.hidden = message === null;
  }

  /** Turn a refusal from core into a sentence in the player's language. */
  private why(problem: NameProblem): string {
    const head = ui(problem.key, this.lang);
    return problem.limit === undefined
      ? head
      : `${head} ${problem.limit} ${ui("slot-long-tail", this.lang)}`;
  }

  private async saveAs(): Promise<void> {
    const name = this.nameInput.value.trim();
    const why = slotNameProblem(name, this.slots.map((s) => s.name));
    if (why !== null) { this.problem(this.why(why)); return; }
    this.problem(null);
    try {
      await this.actions.saveAs(name);
    } catch (error) {
      console.error("save failed", error);
      this.problem(ui("saves-write-failed", this.lang));
      return;
    }
    this.nameInput.value = "";
    await this.refresh();
  }

  private async rename(slot: SlotView): Promise<void> {
    const asked = prompt(`${ui("saves-rename-ask", this.lang)} «${slot.name}»`, slot.name);
    if (asked === null) return;
    const name = asked.trim();
    if (name === slot.name) return;
    const why = slotNameProblem(name, this.slots.map((s) => s.name));
    if (why !== null) { this.problem(this.why(why)); return; }
    this.problem(null);
    try {
      await this.actions.rename(slot.name, name);
    } catch (error) {
      console.error("rename failed", error);
      this.problem(ui("saves-rename-failed", this.lang));
      return;
    }
    await this.refresh();
  }

  private async load(slot: SlotView): Promise<void> {
    const ok = confirm(
      `${ui("saves-load-ask", this.lang)} «${slot.name}»?\n\n` +
      `${describeSlot(slot)}\n\n${ui("saves-load-note", this.lang)}`,
    );
    if (!ok) return;
    await this.actions.load(slot.name);
  }

  private async remove(slot: SlotView): Promise<void> {
    const ok = confirm(
      `${ui("saves-delete-ask", this.lang)} «${slot.name}»?\n\n` +
      `${describeSlot(slot)}\n\n${ui("saves-delete-note", this.lang)}`,
    );
    if (!ok) return;
    try {
      await this.actions.remove(slot.name);
    } catch (error) {
      console.error("delete failed", error);
      this.problem(ui("saves-delete-failed", this.lang));
      return;
    }
    await this.refresh();
  }
}
